import React from 'react';
import { PortableText } from '@portabletext/react';
import type { ArbitraryTypedObject } from '@portabletext/types';
import { PortableTextBlock } from '@sanity/types';
import { getSanityImageUrl } from '@/libs/sanity/client';
import Image from 'next/image';
import { getImageDimensions } from '@sanity/asset-utils';

type PortableTextWrapperProps = {
  value:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  textColor?: string;
};

// Define the proper type for the image component props
type SanityImage = {
  _type?: string;
  asset: {
    _ref: string;
    _type: string;
  };
  alt?: string;
  caption?: string;
};

type SanityImageProps = {
  value: SanityImage;
  isInline?: boolean;
};

const components = {
  types: {
    image: ({ value }: SanityImageProps) => {
      // Add _type to value to satisfy SanityImageSource type
      const imageWithType = {
        _type: 'image',
        asset: {
          _ref: value.asset._ref,
          _type: 'reference',
        },
      };
      const { width, height } = getImageDimensions(imageWithType);
      return (
        <figure className="my-8">
          <Image
            className="min-h-64"
            src={getSanityImageUrl(value)}
            alt={value.alt || 'Blog post image'}
            loading="lazy"
            width={width}
            height={height}
            quality={90}
          />
          {value.caption && (
            <figcaption className="mt-2 text-center text-sm text-gray-500">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
  },
};

const getProseStyles = () =>
  `
    w-full prose max-w-none
    prose-headings:font-normal
    prose-h2:text-[32px] prose-h2:leading-[1.25em] prose-h2:mb-8 md:prose-h2:text-[36px] lg:prose-h2:text-[48px]
    prose-h3:py-4
    prose-h4:font-bold prose-h4:py-4
    prose-h5:font-medium prose-h5:pb-2 md:prose-h5:pb-4 prose-h5:text-xl
    prose-h6:font-medium prose-h6:text-lg
    prose-p:mb-4 md:prose-p:mb-8
    prose-ul:list-disc prose-ul:ml-1 md:prose-ul:ml-4
    prose-li:mb-2 prose-li:pb-2
`.trim();

export function PortableTextWrapper({
  value,
  textColor,
}: PortableTextWrapperProps) {
  if (!value) return null;

  return (
    <div
      className={`${getProseStyles()} ${textColor ? `text-${textColor}` : ''}`}
    >
      <PortableText value={value} components={components} />
    </div>
  );
}
